import type { Review as ApiReview } from '@noor/eats-api';
import type { Review } from '../model/schema';

/**
 * Map a wire review onto its list row.
 *
 * The order label prefers `order_number` — the short, human-facing number the
 * design prints as «Заказ: 67198917». That field is a late addition to
 * `v1Review` and is snapshotted at review-creation time, so a review written
 * before it existed carries `0`; those fall back to the `order_id` UUID, which
 * is ugly but at least addresses a real order. (The dashboard rail predates the
 * field and still shows the UUID — see `entities/merchant-dashboard`.)
 */
export function toReview(review: ApiReview): Review {
  return {
    id: review.id,
    orderLabel: toOrderLabel(review.order_number, review.order_id),
    createdAt: review.created_at ?? '',
    rating: review.rating,
    text: review.comment,
  };
}

/**
 * `order_number` is a protobuf `uint64`, which the gateway may serialize as a
 * JSON number OR a string — both are accepted upstream, and both mean "absent"
 * at zero.
 */
function toOrderLabel(orderNumber: number | string, orderId: string): string {
  const value = typeof orderNumber === 'string' ? orderNumber.trim() : orderNumber;
  if (value !== 0 && value !== '' && value !== '0') return String(value);
  return orderId;
}

/** Weekday-agnostic day key (`YYYY-M-D`) for the "same calendar day" test. */
function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/** `HH:mm`, zero-padded. */
function clock(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/** Strings the caller supplies so the formatter itself stays locale-free. */
export interface ReviewTimestampLabels {
  /** «Сегодня в {{time}}» */
  today: string;
  /** «Вчера в {{time}}» */
  yesterday: string;
  /** «{{day}} {{month}} в {{time}}» */
  date: string;
  /** 12 month names in the form the design prints («май», not «Май»). */
  months: string[];
}

/**
 * The design's relative timestamp: «Сегодня в 12:50», «Вчера в 12:50», else
 * «1 май в 09:40» (Figma 26295:98075 / 98103 / 98116).
 *
 * Relative to `now`, which is injected rather than read from the clock so the
 * function is pure and testable — "today" is a moving target, and a test that
 * calls `new Date()` inside the unit under test can only assert against itself.
 *
 * Returns `''` for a missing or unparseable timestamp: the row then shows no
 * caption rather than "Invalid Date".
 */
export function formatReviewTimestamp(
  timestamp: string,
  labels: ReviewTimestampLabels,
  now: Date,
): string {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const time = clock(date);
  if (dayKey(date) === dayKey(now)) {
    return labels.today.replace('{{time}}', time);
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dayKey(date) === dayKey(yesterday)) {
    return labels.yesterday.replace('{{time}}', time);
  }

  return labels.date
    .replace('{{day}}', String(date.getDate()))
    .replace('{{month}}', labels.months[date.getMonth()] ?? '')
    .replace('{{time}}', time);
}
